import { useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { acceptInvite } from "@/lib/api/orgAdminInvite";
import { supabase } from "@/lib/supabase";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";

const INVITE_TOKEN_HEX = /^[0-9a-f]{64}$/i;

function normalizeInviteTokenFromUrl(raw: string): string {
  let t = raw.trim();
  try {
    t = decodeURIComponent(t);
  } catch {
    // ignore malformed percent-encoding
  }
  return t.replace(/\s+/g, "").trim();
}

export function SetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(
    () => normalizeInviteTokenFromUrl(searchParams.get("token") ?? ""),
    [searchParams],
  );
  const inviteSource = useMemo((): "employee" | "org_admin" | "portal_contact" | "staff" | undefined => {
    const v = (searchParams.get("invite_source") ?? "").replace(/^\uFEFF/, "").trim().toLowerCase();
    if (v === "employee" || v === "org_admin" || v === "staff") return v;
    if (v === "portal_contact" || v === "portal-contact" || v === "portal") return "portal_contact";
    return undefined;
  }, [searchParams]);

  const tokenLooksBroken = token.length > 0 && !INVITE_TOKEN_HEX.test(token);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);

    if (!token) {
      setError("Invalid or missing invite link.");
      return;
    }
    if (!INVITE_TOKEN_HEX.test(token)) {
      setError(
        "This invite link looks broken or truncated (token must be 64 hex characters). Copy the full link from your email, or ask your administrator to resend the invite.",
      );
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const result = await acceptInvite(token, password, inviteSource);
      if (!result.ok) {
        setError(result.message);
        return;
      }

      const { error: signErr } = await supabase.auth.signInWithPassword({
        email: result.email,
        password,
      });
      if (signErr) {
        navigate("/login", { replace: true });
        return;
      }
      navigate("/", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-bg)] p-4">
      <Card className="w-full max-w-md border border-[var(--color-border-muted)] bg-[var(--color-surface)] p-6">
        <p className="font-[var(--font-display)] text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-highlight-blue)]">
          Fit2Lift invite
        </p>
        <h1 className="mt-2 font-[var(--font-display)] text-xl font-bold uppercase tracking-[0.04em] text-[var(--color-text)]">
          Set your password
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-[var(--color-text-muted)]">
          Choose a password to activate your Fit2Lift account. This link works once.
        </p>

        <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
          {error ? (
            <Alert variant="error" role="alert">
              {error}
            </Alert>
          ) : null}

          {!token ? (
            <Alert variant="error" role="alert">
              Invalid or missing invite link. Open the link from your email or ask your Fit2Lift administrator to resend
              the invite.
            </Alert>
          ) : null}
          {tokenLooksBroken ? (
            <Alert variant="error" role="alert">
              This invite link looks broken or truncated. Use the full URL from your email (64-character token after{" "}
              <span className="font-[var(--font-mono)] text-xs">token=</span>
              ), or ask your administrator to resend the invite.
            </Alert>
          ) : null}

          <div>
            <Label htmlFor="set-pw" className="text-[var(--color-text-subtle)]">
              Password
            </Label>
            <Input
              id="set-pw"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(ev) => setPassword(ev.target.value)}
              required
              minLength={8}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="set-pw2" className="text-[var(--color-text-subtle)]">
              Confirm password
            </Label>
            <Input
              id="set-pw2"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(ev) => setConfirm(ev.target.value)}
              required
              minLength={8}
              className="mt-1"
            />
          </div>

          <Button
            type="submit"
            loading={loading}
            className="mt-2 w-full justify-center"
            disabled={!token || tokenLooksBroken}
          >
            Activate account
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-[var(--color-text-muted)]">
          <Link to="/login" className="text-[var(--color-signal-blue)] hover:underline">
            Back to sign in
          </Link>
        </p>
      </Card>
    </div>
  );
}
